import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, LocateFixed, Clock, Route, Check, Copy, Phone, Star, MessageCircle, Coins } from "lucide-react";
import "./transport.css";
import { BookingHeader, PrimaryBtn, PriceRow } from "./components";
import { TAXI_PLACES, TAXI_TYPES, taxiEstimate, fmt, makeRef } from "./transportData";
import { saveTicket, updateTicketDetails } from "./ticketStore";
import MobileMoneyCheckout from "@/components/payments/MobileMoneyCheckout";

const API = process.env.REACT_APP_BACKEND_URL || "";
const PAY_METHODS = [
  { id: "CASH", name: "Espèces", desc: "Remis directement au chauffeur", logo: "FCFA" },
  { id: "WALLET", name: "Wallet AYA", desc: "Solde débité instantanément", logo: "AYA" },
  { id: "MOBILE_MONEY", name: "Mobile Money", desc: "Orange · Wave · MTN (sandbox)", logo: "MM" },
];

const STEPS = ["locations", "estimate", "propose", "searching", "counter", "tracking", "done"];
const TITLES = {
  locations: "Réserver un taxi", estimate: "Estimation du trajet", propose: "Proposer un prix",
  searching: "Recherche d'un chauffeur", counter: "Contre-proposition", tracking: "Course acceptée", done: "Fin de course",
};
const DRIVER = { name: "Jean K.", rating: "4,8", rides: 128, car: "Toyota Corolla", plate: "AB 1234 CI" };

export default function TaxiBookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("locations");
  const [depart, setDepart] = useState(TAXI_PLACES[0]);
  const [dest, setDest] = useState(null);
  const [destQuery, setDestQuery] = useState("");
  const [taxiType, setTaxiType] = useState("standard");
  const [proposed, setProposed] = useState(null);
  const [message, setMessage] = useState("");
  const [counterPrice, setCounterPrice] = useState(null);
  const [agreed, setAgreed] = useState(null);
  const [phase, setPhase] = useState("approche");
  const [eta, setEta] = useState(3);
  const [progress, setProgress] = useState(0);
  const [rating, setRating] = useState(0);
  const [rideRef, setRideRef] = useState(null);
  const savedRef = useRef(false);
  // PHASE V2 — Paiement fin de course (backend : Payment Engine + Fiscal Engine).
  const [payMethod, setPayMethod] = useState("CASH");
  const [payState, setPayState] = useState("idle"); // idle | loading | mm | paid | error
  const [payError, setPayError] = useState(null);
  const [mmPayment, setMmPayment] = useState(null);
  const [ride, setRide] = useState(null);

  const km = dest ? Math.max(1.2, Math.abs(dest.km - depart.km)) : 0;
  const type = TAXI_TYPES.find((t) => t.id === taxiType);
  const est = taxiEstimate(km, type.mult);

  const suggestions = destQuery.trim()
    ? TAXI_PLACES.filter((p) => p.id !== depart.id && (p.name + p.commune).toLowerCase().includes(destQuery.toLowerCase()))
    : TAXI_PLACES.filter((p) => p.id !== depart.id).slice(0, 5);

  const goBack = () => {
    if (step === "done" || step === "tracking") return navigate("/search");
    if (step === "counter" || step === "searching") return setStep("propose");
    const i = STEPS.indexOf(step);
    if (i === 0) return navigate("/search");
    setStep(STEPS[i - 1]);
  };

  const sendProposal = () => setStep("searching");

  // Décision chauffeur simulée (remplaçable par vraie API dispatch)
  useEffect(() => {
    if (step !== "searching") return;
    const t = setTimeout(() => {
      if (proposed >= est.price * 0.9) {
        setAgreed(proposed);
        setRideRef(makeRef("AYA-TX", depart.commune.slice(0, 3).toUpperCase(), dest.commune.slice(0, 3).toUpperCase()));
        setPhase("approche"); setEta(3); setProgress(0);
        setStep("tracking");
      } else {
        setCounterPrice(est.price);
        setStep("counter");
      }
    }, 2200);
    return () => clearTimeout(t);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const acceptCounter = () => {
    setAgreed(counterPrice);
    setRideRef(makeRef("AYA-TX", depart.commune.slice(0, 3).toUpperCase(), dest.commune.slice(0, 3).toUpperCase()));
    setPhase("approche"); setEta(3); setProgress(0);
    setStep("tracking");
  };

  // Suivi simulé : approche (ETA décroissant) → arrivé → en cours (progression) → terminé
  useEffect(() => {
    if (step !== "tracking") return;
    if (phase === "approche") {
      const iv = setInterval(() => setEta((e) => {
        if (e <= 1) { clearInterval(iv); setPhase("arrive"); return 0; }
        return e - 1;
      }), 1500);
      return () => clearInterval(iv);
    }
    if (phase === "encours") {
      const iv = setInterval(() => setProgress((p) => {
        if (p >= 100) { clearInterval(iv); setStep("done"); return 100; }
        return p + 10;
      }), 600);
      return () => clearInterval(iv);
    }
  }, [step, phase]);

  useEffect(() => {
    if (step === "done" && !savedRef.current && dest) {
      savedRef.current = true;
      saveTicket({
        type: "taxi", ref: rideRef, title: `${depart.commune} → ${dest.commune}`,
        sub: `Taxi · ${DRIVER.name} · ${km.toFixed(1).replace(".", ",")} km`, price: agreed,
        details: { Départ: depart.name, Destination: dest.name, Chauffeur: `${DRIVER.name} (${DRIVER.car})`, Montant: fmt(agreed) },
      });
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const kmLeft = ((100 - progress) / 100 * km).toFixed(1).replace(".", ",");

  // PHASE V2 — RIDE_COMPLETED → prix final → paiement → fiscal (backend sandbox).
  const payRide = async () => {
    if (payState === "loading") return;
    const token = localStorage.getItem("civ_user_token") || "";
    if ((payMethod === "WALLET" || payMethod === "MOBILE_MONEY") && !token) {
      toast.error("Connecte-toi pour payer par " + (payMethod === "WALLET" ? "Wallet AYA" : "Mobile Money"));
      return;
    }
    setPayState("loading");
    setPayError(null);
    // uid anonyme garanti (CASH sans compte) — normalement initialisé par useUserId.
    let anonId = localStorage.getItem("jsonfirst_civ_userid");
    if (!anonId) {
      anonId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `anon-${Date.now()}`;
      try { localStorage.setItem("jsonfirst_civ_userid", anonId); } catch (_) { /* noop */ }
    }
    try {
      const r = await fetch(`${API}/api/v3/taxi/rides/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          client_ref: rideRef,
          user_id: anonId,
          origin: { name: depart.name, commune: depart.commune },
          destination: { name: dest.name, commune: dest.commune },
          distance_km: Number(km.toFixed(1)),
          taxi_type: taxiType,
          estimated_fare: est.price,
          final_fare: agreed,
          payment_method: payMethod,
          driver: { id: "drv_jean_k", name: DRIVER.name, vehicle: DRIVER.plate },
        }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) {
        setPayError(b.error === "wallet_debit_failed" ? "Solde Wallet insuffisant" : (b.error || "Paiement impossible"));
        setPayState("idle");
        return;
      }
      setRide(b.ride);
      if (payMethod === "MOBILE_MONEY" && b.payment && b.payment.status !== "PAYMENT_CONFIRMED") {
        setMmPayment(b.payment);
        setPayState("mm");
        return;
      }
      updateTicketDetails(rideRef, {
        Paiement: `Payé ✓ · ${PAY_METHODS.find((m) => m.id === payMethod)?.name || payMethod}`,
        ...(b.ride?.fiscal?.number ? { "Reçu fiscal": `${b.ride.fiscal.type} ${b.ride.fiscal.number}` } : { "Reçu fiscal": "En vérification" }),
      });
      setPayState("paid");
    } catch (_) {
      setPayError("Erreur réseau");
      setPayState("idle");
    }
  };

  const fiscalLabel = (f) => {
    if (!f) return "Reçu fiscal en préparation…";
    if (f.status === "FISCAL_CERTIFIED") return `Reçu fiscal ${f.type} ${f.number} (SANDBOX)`;
    if (f.status === "FISCAL_REQUIRES_REVIEW") return "Reçu fiscal en cours de vérification";
    if (f.status === "FISCAL_EXEMPT") return "Transaction exonérée";
    return "Reçu fiscal en préparation…";
  };

  return (
    <div className="tp-page" data-testid="taxi-booking-page">
      <BookingHeader title={TITLES[step]} onBack={goBack} />
      <div className="tp-container">

        {step === "locations" && (
          <>
            <div className="tp-sechead"><div><h2>Point de départ</h2><p>Adressage AYA / PADA</p></div></div>
            <div className="tp-input" data-testid="taxi-depart">
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <LocateFixed size={15} color="#0E7A4F" />
                <span style={{ flex: 1 }}>
                  <label>Ma position actuelle</label>
                  <select value={depart.id} onChange={(e) => setDepart(TAXI_PLACES.find((p) => p.id === e.target.value))} data-testid="taxi-depart-select">
                    {TAXI_PLACES.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.commune}</option>)}
                  </select>
                </span>
              </span>
            </div>
            <div className="tp-sechead"><div><h2>Destination</h2></div></div>
            <label className="tp-input" data-testid="taxi-dest-input-wrap">
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MapPin size={15} color="#DC2626" />
                <span style={{ flex: 1 }}>
                  <label>Où allez-vous ?</label>
                  <input value={dest ? dest.name : destQuery} data-testid="taxi-dest-input"
                    onChange={(e) => { setDest(null); setDestQuery(e.target.value); }}
                    placeholder="Rechercher une adresse (PADA)..." />
                </span>
              </span>
            </label>
            {!dest && (
              <div className="tp-suggest" data-testid="taxi-suggestions">
                {suggestions.map((p) => (
                  <button key={p.id} data-testid={`taxi-suggest-${p.id}`} onClick={() => { setDest(p); setDestQuery(""); }}>
                    <MapPin size={13} color="#0E7A4F" /> {p.name} <span style={{ color: "#A6B2A9", fontSize: 10.5 }}>· {p.commune}</span>
                  </button>
                ))}
              </div>
            )}
            <PrimaryBtn testid="taxi-estimate-btn" disabled={!dest} onClick={() => setStep("estimate")}>Voir l'estimation</PrimaryBtn>
          </>
        )}

        {step === "estimate" && dest && (
          <>
            <div className="tp-card">
              <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 3 }}>
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#0E7A4F" }} />
                  <span style={{ width: 2, height: 22, background: "#DCE5DD" }} />
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#DC2626" }} />
                </span>
                <span style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, marginBottom: 11 }}>{depart.name}<br /><span style={{ fontSize: 10, color: "#7B8B7F", fontWeight: 600 }}>{depart.commune}</span></div>
                  <div style={{ fontSize: 12.5, fontWeight: 800 }}>{dest.name}<br /><span style={{ fontSize: 10, color: "#7B8B7F", fontWeight: 600 }}>{dest.commune}</span></div>
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }} data-testid="taxi-estimate-stats">
              <div className="tp-card" style={{ flex: 1, marginBottom: 0, textAlign: "center", padding: "9px 4px" }}>
                <Route size={15} color="#0E7A4F" /><div style={{ fontSize: 13, fontWeight: 900 }}>{km.toFixed(1).replace(".", ",")} km</div><div style={{ fontSize: 9.5, color: "#7B8B7F" }}>Distance</div>
              </div>
              <div className="tp-card" style={{ flex: 1, marginBottom: 0, textAlign: "center", padding: "9px 4px" }}>
                <Clock size={15} color="#0E7A4F" /><div style={{ fontSize: 13, fontWeight: 900 }}>~ {est.minutes} min</div><div style={{ fontSize: 9.5, color: "#7B8B7F" }}>Durée estimée</div>
              </div>
              <div className="tp-card" style={{ flex: 1, marginBottom: 0, textAlign: "center", padding: "9px 4px" }}>
                <span style={{ fontSize: 13, fontWeight: 900, color: "#0E7A4F" }}>{fmt(est.price)}</span><div style={{ fontSize: 9.5, color: "#7B8B7F" }}>Tarif estimé</div>
              </div>
            </div>
            <div className="tp-sechead"><div><h2>Type de taxi</h2></div></div>
            {TAXI_TYPES.map((t) => {
              const p = taxiEstimate(km, t.mult).price;
              return (
                <div key={t.id} className={`tp-payrow ${taxiType === t.id ? "on" : ""}`} data-testid={`taxi-type-${t.id}`} onClick={() => setTaxiType(t.id)}>
                  <span className="tp-paylogo" style={{ background: taxiType === t.id ? "#0E7A4F" : "#94A89B" }}>TAXI</span>
                  <span style={{ flex: 1 }}><h4 style={{ fontSize: 12.5, fontWeight: 800, margin: 0 }}>{t.name}</h4>
                    <span style={{ fontSize: 10, color: "#7B8B7F" }}>{t.desc}</span></span>
                  <b style={{ fontSize: 12.5, color: "#0E7A4F" }}>{fmt(p)}</b>
                </div>
              );
            })}
            <PrimaryBtn testid="taxi-continue-btn" onClick={() => { setProposed(est.price); setStep("propose"); }}>Proposer un prix</PrimaryBtn>
          </>
        )}

        {step === "propose" && dest && (
          <>
            <div className="tp-summary">
              <span className="tp-paylogo" style={{ background: "#0E7A4F", width: 30, height: 30, borderRadius: 9 }}><Coins size={14} /></span>
              <span className="t"><h3>{depart.commune} → {dest.commune}</h3><p>{km.toFixed(1).replace(".", ",")} km · ~ {est.minutes} min · Tarif estimé {fmt(est.price)}</p></span>
            </div>
            <div className="tp-sechead"><div><h2>Votre proposition</h2><p>Le chauffeur peut accepter ou contre-proposer</p></div></div>
            <div className="tp-card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }} data-testid="taxi-propose-box">
              <button className="tp-back" data-testid="taxi-price-minus" aria-label="Diminuer"
                onClick={() => setProposed((p) => Math.max(500, p - 100))} style={{ fontSize: 18, fontWeight: 800 }}>−</button>
              <b style={{ fontSize: 21 }} data-testid="taxi-proposed-price">{fmt(proposed)}</b>
              <button className="tp-back" data-testid="taxi-price-plus" aria-label="Augmenter"
                onClick={() => setProposed((p) => p + 100)} style={{ fontSize: 18, fontWeight: 800 }}>+</button>
            </div>
            <div style={{ textAlign: "center", fontSize: 10.5, fontWeight: 700, marginBottom: 9, color: proposed >= est.price ? "#0E7A4F" : "#B45309" }} data-testid="taxi-propose-diff">
              {proposed === est.price ? "Prix identique au tarif estimé"
                : proposed > est.price ? `+ ${fmt(proposed - est.price)} par rapport au tarif estimé`
                  : `− ${fmt(est.price - proposed)} par rapport au tarif estimé`}
            </div>
            <label className="tp-input"><label>Message (optionnel)</label>
              <input value={message} onChange={(e) => setMessage(e.target.value)} data-testid="taxi-message-input" placeholder="Ex : Je suis devant la pharmacie" />
            </label>
            <PrimaryBtn testid="taxi-send-proposal" onClick={sendProposal}>Envoyer ma proposition</PrimaryBtn>
          </>
        )}

        {step === "searching" && (
          <div className="tp-confirm" data-testid="taxi-searching">
            <span className="circ" style={{ background: "#fff", border: "3px solid #10B981", animation: "pulse 1s infinite" }}>
              <Coins size={30} color="#0E7A4F" />
            </span>
            <h2>Recherche d'un chauffeur…</h2>
            <p>Proposition envoyée : <b>{fmt(proposed)}</b><br />Les chauffeurs partenaires à proximité examinent votre offre.</p>
          </div>
        )}

        {step === "counter" && (
          <>
            <div className="tp-confirm" style={{ padding: "16px 0 4px" }}>
              <h2 style={{ fontSize: 16 }}>Le chauffeur propose un autre prix</h2>
            </div>
            <div className="tp-card" style={{ textAlign: "center" }} data-testid="taxi-counter-box">
              <div style={{ fontSize: 10.5, color: "#7B8B7F", fontWeight: 700 }}>Votre proposition</div>
              <div style={{ fontSize: 14, fontWeight: 800, textDecoration: "line-through", color: "#A6B2A9" }}>{fmt(proposed)}</div>
              <div style={{ fontSize: 10.5, color: "#7B8B7F", fontWeight: 700, marginTop: 8 }}>Contre-proposition de {DRIVER.name}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#0E7A4F" }} data-testid="taxi-counter-price">{fmt(counterPrice)}</div>
            </div>
            <PrimaryBtn testid="taxi-accept-counter" onClick={acceptCounter}>Accepter {fmt(counterPrice)}</PrimaryBtn>
            <div style={{ height: 8 }} />
            <PrimaryBtn ghost testid="taxi-modify-proposal" onClick={() => setStep("propose")}>Modifier ma proposition</PrimaryBtn>
          </>
        )}

        {step === "tracking" && dest && (
          <>
            <div className="tp-card" style={{ display: "flex", alignItems: "center", gap: 10 }} data-testid="taxi-driver-card">
              <span className="tp-logo" style={{ background: "#0E7A4F", width: 40, height: 40, borderRadius: "50%", fontSize: 13 }}>JK</span>
              <span style={{ flex: 1 }}>
                <b style={{ fontSize: 13 }}>{DRIVER.name}</b>
                <div style={{ fontSize: 10.5, color: "#7B8B7F", display: "flex", alignItems: "center", gap: 3 }}>
                  <Star size={10} color="#F5A623" fill="#F5A623" /> {DRIVER.rating} ({DRIVER.rides} courses) · {DRIVER.car} · {DRIVER.plate}
                </div>
              </span>
              <button className="tp-back" data-testid="taxi-call-driver" aria-label="Appeler" onClick={() => toast.info("Mise en relation avec le chauffeur…")}><Phone size={14} /></button>
              <button className="tp-back" data-testid="taxi-msg-driver" aria-label="Message" onClick={() => toast.info("Messagerie chauffeur bientôt disponible.")}><MessageCircle size={14} /></button>
            </div>

            {phase === "approche" && (
              <>
                <div className="tp-card" style={{ textAlign: "center" }} data-testid="taxi-eta">
                  <div style={{ fontSize: 10.5, color: "#7B8B7F", fontWeight: 700 }}>Rendez-vous au point de prise en charge</div>
                  <div style={{ fontSize: 19, fontWeight: 900, color: "#0E7A4F", marginTop: 3 }}>Arrivée dans ~ {eta} min</div>
                  <div style={{ fontSize: 10.5, color: "#7B8B7F", marginTop: 2 }}>{depart.name}</div>
                </div>
                <div className="tp-card"><PriceRow total label="Prix convenu" value={fmt(agreed)} /></div>
              </>
            )}

            {phase === "arrive" && (
              <>
                <div className="tp-card" style={{ textAlign: "center", border: "1.5px solid #10B981", background: "#F3FBF6" }} data-testid="taxi-arrived">
                  <div style={{ fontSize: 14.5, fontWeight: 900, color: "#0E7A4F" }}>Votre chauffeur est arrivé</div>
                  <div style={{ fontSize: 10.5, color: "#4A5D50", marginTop: 2 }}>{DRIVER.car} · {DRIVER.plate}</div>
                </div>
                <PrimaryBtn testid="taxi-start-ride" onClick={() => setPhase("encours")}>Démarrer la course</PrimaryBtn>
              </>
            )}

            {phase === "encours" && (
              <>
                <div className="tp-card" data-testid="taxi-progress">
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 800, marginBottom: 7 }}>
                    <span>Course en cours</span><span style={{ color: "#0E7A4F" }}>{progress} %</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 999, background: "#E4EBE5", overflow: "hidden" }}>
                    <div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg,#10B981,#0E7A4F)", transition: "width 0.5s" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 10.5, color: "#7B8B7F" }}>
                    <span><Route size={10} style={{ verticalAlign: -1 }} /> {kmLeft} km restants</span>
                    <span><Clock size={10} style={{ verticalAlign: -1 }} /> Destination : {dest.commune}</span>
                  </div>
                </div>
                <div className="tp-card"><PriceRow total label="Prix convenu" value={fmt(agreed)} /></div>
              </>
            )}
          </>
        )}

        {step === "done" && dest && (
          <>
            <div className="tp-confirm" data-testid="taxi-done">
              <span className="circ"><Check size={36} color="#fff" strokeWidth={3} /></span>
              <h2>Course terminée avec succès !</h2>
              <p>Merci d'avoir voyagé avec AYA.</p>
            </div>
            <div className="tp-ref" data-testid="taxi-ride-ref">
              <span><span className="lab">Référence course</span>{rideRef}</span>
              <button className="tp-link" onClick={() => { navigator.clipboard?.writeText(rideRef); toast.success("Référence copiée ✓"); }} aria-label="Copier"><Copy size={14} /></button>
            </div>
            <div className="tp-card">
              <PriceRow label="Trajet" value={`${depart.commune} → ${dest.commune}`} />
              <PriceRow label="Chauffeur" value={DRIVER.name} />
              <PriceRow total label="Montant de la course" value={fmt(agreed)} />
            </div>
            {payState !== "paid" ? (
              <>
                <div className="tp-sechead"><div><h2>Paiement de la course</h2><p>Le reçu fiscal est généré automatiquement</p></div></div>
                {PAY_METHODS.map((m) => (
                  <div key={m.id} className={`tp-payrow ${payMethod === m.id ? "on" : ""}`} data-testid={`taxi-pay-${m.id}`}
                    onClick={() => payState !== "loading" && payState !== "mm" && setPayMethod(m.id)}>
                    <span className="tp-paylogo" style={{ background: payMethod === m.id ? "#0E7A4F" : "#94A89B" }}>{m.logo}</span>
                    <span style={{ flex: 1 }}><h4 style={{ fontSize: 12.5, fontWeight: 800, margin: 0 }}>{m.name}</h4>
                      <span style={{ fontSize: 10, color: "#7B8B7F" }}>{m.desc}</span></span>
                    {payMethod === m.id && <Check size={15} color="#0E7A4F" />}
                  </div>
                ))}
                {payError && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#B91C1C", textAlign: "center", marginBottom: 8 }} data-testid="taxi-pay-error">⚠️ {payError}</div>
                )}
                {payState === "mm" && mmPayment ? (
                  <MobileMoneyCheckout
                    amountFcfa={agreed}
                    existingPayment={mmPayment}
                    onCancel={() => { setMmPayment(null); setPayState("idle"); }}
                    onConfirmed={(result) => {
                      setRide((prev) => ({ ...(prev || {}), paymentStatus: result?.paymentStatus || "PAYMENT_CONFIRMED", fiscal: result?.fiscal || null }));
                      updateTicketDetails(rideRef, {
                        Paiement: "Payé ✓ · Mobile Money",
                        ...(result?.fiscal?.number ? { "Reçu fiscal": `${result.fiscal.type} ${result.fiscal.number}` } : { "Reçu fiscal": "En vérification" }),
                      });
                      setPayState("paid");
                    }}
                  />
                ) : (
                  <PrimaryBtn testid="taxi-pay-confirm" disabled={payState === "loading"} onClick={payRide}>
                    {payState === "loading" ? "Paiement en cours…" : `Confirmer le paiement · ${fmt(agreed)}`}
                  </PrimaryBtn>
                )}
                <div style={{ height: 8 }} />
              </>
            ) : (
              <div className="tp-card" style={{ border: "1.5px solid #10B981", background: "#F3FBF6" }} data-testid="taxi-payment-done">
                <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 900, color: "#0E7A4F" }}>
                  <Check size={15} strokeWidth={3} /> Paiement confirmé · {PAY_METHODS.find((m) => m.id === (ride?.paymentMethod || payMethod))?.name || payMethod}
                </div>
                <div style={{ fontSize: 10.5, color: "#4A5D50", marginTop: 4 }} data-testid="taxi-fiscal-status">
                  🧾 {fiscalLabel(ride?.fiscal)}
                </div>
              </div>
            )}
            <div className="tp-card" style={{ textAlign: "center" }} data-testid="taxi-rating">
              <div style={{ fontSize: 11.5, fontWeight: 800, marginBottom: 6 }}>Noter le chauffeur</div>
              <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} data-testid={`taxi-star-${s}`} onClick={() => { setRating(s); toast.success(`Merci pour votre note (${s}/5) !`); }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                    <Star size={26} color="#F5A623" fill={s <= rating ? "#F5A623" : "none"} />
                  </button>
                ))}
              </div>
            </div>
            <PrimaryBtn testid="taxi-see-tickets" onClick={() => navigate("/mes-billets")}>Voir mes billets</PrimaryBtn>
            <div style={{ height: 8 }} />
            <PrimaryBtn ghost testid="taxi-done-home" onClick={() => navigate("/search")}>Retour à l'accueil</PrimaryBtn>
          </>
        )}
      </div>
    </div>
  );
}
